import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Smartphone, Building, CheckCircle, Lock } from 'lucide-react';
import Header from './components/Header20';
import Footer from './components/Footer20';
import Button from './components/Button';

import { devLog } from '../../../utils/devLog';
const Checkout = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [paymentMethod, setPaymentMethod] = useState('card');

    const [formData, setFormData] = useState({
        // Shipping Information
        fullName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        pincode: '',

        // Payment Information
        cardNumber: '',
        cardName: '',
        expiryDate: '',
        cvv: '',
        upiId: '',
    });

    // Sample order summary
    const orderSummary = {
        items: [
            { name: 'Abstract Expression Tee', quantity: 2, price: 2598 },
            { name: 'Urban Nights Hoodie', quantity: 1, price: 2499 },
        ],
        subtotal: 5097,
        discount: 510,
        shipping: 0,
        total: 4587,
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (currentStep === 1) {
            setCurrentStep(2);
        } else if (currentStep === 2) {
            // Process payment
            devLog.log('Processing order:', formData);
            setCurrentStep(3);
        }
    };

    // Success Screen
    if (currentStep === 3) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex flex-col">
                <Header cartCount={0} />
                <div className="flex-1 flex items-center justify-center px-4 py-12">
                    <div className="bg-white rounded-3xl p-8 lg:p-12 max-w-2xl w-full text-center shadow-2xl">
                        <div className="mb-6">
                            <CheckCircle size={80} className="text-green-500 mx-auto" />
                        </div>
                        <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                            Order Placed Successfully!
                        </h1>
                        <p className="text-gray-600 text-lg mb-2">
                            Thank you for your purchase!
                        </p>
                        <p className="text-gray-500 mb-8">
                            Order ID: <span className="font-semibold text-orange-600">#ORD{Math.floor(Math.random() * 100000)}</span>
                        </p>
                        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 mb-8">
                            <p className="text-gray-700 mb-2">
                                We've sent a confirmation email to <span className="font-semibold">{formData.email}</span>
                            </p>
                            <p className="text-gray-600 text-sm">
                                Your order will be delivered within 5-7 business days
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button variant="primary" size="lg" onClick={() => navigate('/template20/orders')}>
                                Track Order
                            </Button>
                            <Button variant="outline" size="lg" onClick={() => navigate('/template20/shop')}>
                                Continue Shopping
                            </Button>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
            <Header showBackButton={true} cartCount={2} />

            <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-12">
                <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-8 text-center">
                    Checkout
                </h1>

                {/* Progress Steps */}
                <div className="max-w-3xl mx-auto mb-12">
                    <div className="flex items-center justify-center gap-4">
                        <div className="flex items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                                currentStep >= 1 ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white' : 'bg-gray-200 text-gray-500'
                            }`}>
                                1
                            </div>
                            <span className="ml-2 font-medium text-gray-700 hidden sm:inline">Shipping</span>
                        </div>
                        <div className={`h-1 w-16 ${currentStep >= 2 ? 'bg-gradient-to-r from-orange-500 to-amber-500' : 'bg-gray-200'}`}></div>
                        <div className="flex items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                                currentStep >= 2 ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white' : 'bg-gray-200 text-gray-500'
                            }`}>
                                2
                            </div>
                            <span className="ml-2 font-medium text-gray-700 hidden sm:inline">Payment</span>
                        </div>
                        <div className={`h-1 w-16 ${currentStep >= 3 ? 'bg-gradient-to-r from-orange-500 to-amber-500' : 'bg-gray-200'}`}></div>
                        <div className="flex items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                                currentStep >= 3 ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white' : 'bg-gray-200 text-gray-500'
                            }`}>
                                3
                            </div>
                            <span className="ml-2 font-medium text-gray-700 hidden sm:inline">Complete</span>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Checkout Form */}
                    <div className="lg:col-span-2">
                        <form onSubmit={handleSubmit}>
                            {/* Shipping Information */}
                            {currentStep === 1 && (
                                <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg">
                                    <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                                        Shipping Information
                                    </h2>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Full Name *
                                            </label>
                                            <input
                                                type="text"
                                                name="fullName"
                                                value={formData.fullName}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                placeholder="John Doe"
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    Email *
                                                </label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                    placeholder="john@example.com"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    Phone Number *
                                                </label>
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                    placeholder="+91 98765 43210"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Address *
                                            </label>
                                            <textarea
                                                name="address"
                                                value={formData.address}
                                                onChange={handleInputChange}
                                                required
                                                rows="3"
                                                className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                placeholder="Street address, apartment, suite, etc."
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    City *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                    placeholder="Mumbai"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    State *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="state"
                                                    value={formData.state}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                    placeholder="Maharashtra"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    PIN Code *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="pincode"
                                                    value={formData.pincode}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                    placeholder="400001"
                                                />
                                            </div>
                                        </div>

                                        <Button type="submit" variant="primary" size="lg" fullWidth={true}>
                                            Continue to Payment
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Payment Information */}
                            {currentStep === 2 && (
                                <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg">
                                    <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                                        Payment Method
                                    </h2>

                                    {/* Payment Method Selection */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                        <button
                                            type="button"
                                            onClick={() => setPaymentMethod('card')}
                                            className={`p-4 rounded-xl border-2 transition-all ${
                                                paymentMethod === 'card'
                                                    ? 'border-orange-500 bg-orange-50'
                                                    : 'border-gray-200 hover:border-orange-300'
                                            }`}
                                        >
                                            <CreditCard className="mx-auto mb-2 text-orange-600" size={32} />
                                            <p className="font-semibold text-gray-900">Card</p>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setPaymentMethod('upi')}
                                            className={`p-4 rounded-xl border-2 transition-all ${
                                                paymentMethod === 'upi'
                                                    ? 'border-orange-500 bg-orange-50'
                                                    : 'border-gray-200 hover:border-orange-300'
                                            }`}
                                        >
                                            <Smartphone className="mx-auto mb-2 text-orange-600" size={32} />
                                            <p className="font-semibold text-gray-900">UPI</p>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setPaymentMethod('netbanking')}
                                            className={`p-4 rounded-xl border-2 transition-all ${
                                                paymentMethod === 'netbanking'
                                                    ? 'border-orange-500 bg-orange-50'
                                                    : 'border-gray-200 hover:border-orange-300'
                                            }`}
                                        >
                                            <Building className="mx-auto mb-2 text-orange-600" size={32} />
                                            <p className="font-semibold text-gray-900">Net Banking</p>
                                        </button>
                                    </div>

                                    {/* Card Payment Form */}
                                    {paymentMethod === 'card' && (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    Card Number *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="cardNumber"
                                                    value={formData.cardNumber}
                                                    onChange={handleInputChange}
                                                    required
                                                    maxLength="19"
                                                    className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                    placeholder="1234 5678 9012 3456"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    Cardholder Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="cardName"
                                                    value={formData.cardName}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                    placeholder="JOHN DOE"
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Expiry Date *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="expiryDate"
                                                        value={formData.expiryDate}
                                                        onChange={handleInputChange}
                                                        required
                                                        maxLength="5"
                                                        className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                        placeholder="MM/YY"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        CVV *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="cvv"
                                                        value={formData.cvv}
                                                        onChange={handleInputChange}
                                                        required
                                                        maxLength="3"
                                                        className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                        placeholder="123"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* UPI Payment Form */}
                                    {paymentMethod === 'upi' && (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    UPI ID *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="upiId"
                                                    value={formData.upiId}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                    placeholder="yourname@upi"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Net Banking */}
                                    {paymentMethod === 'netbanking' && (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    Select Bank *
                                                </label>
                                                <select
                                                    required
                                                    className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                >
                                                    <option value="">Choose your bank</option>
                                                    <option value="hdfc">HDFC Bank</option>
                                                    <option value="icici">ICICI Bank</option>
                                                    <option value="sbi">State Bank of India</option>
                                                    <option value="axis">Axis Bank</option>
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-6 flex items-center gap-2 text-sm text-gray-600">
                                        <Lock size={16} className="text-green-600" />
                                        <span>Your payment information is secure and encrypted</span>
                                    </div>

                                    <div className="flex gap-4 mt-6">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="lg"
                                            onClick={() => setCurrentStep(1)}
                                        >
                                            Back
                                        </Button>
                                        <Button type="submit" variant="primary" size="lg" fullWidth={true}>
                                            Place Order
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>

                    {/* Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-2xl p-6 shadow-lg sticky top-24">
                            <h2 className="text-xl font-bold mb-6 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                                Order Summary
                            </h2>

                            {/* Items */}
                            <div className="space-y-3 mb-6 pb-6 border-b border-gray-200">
                                {orderSummary.items.map((item, index) => (
                                    <div key={index} className="flex justify-between text-sm">
                                        <span className="text-gray-700">
                                            {item.name} x{item.quantity}
                                        </span>
                                        <span className="font-medium">₹{item.price}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Price Breakdown */}
                            <div className="space-y-3 mb-6 pb-6 border-b border-gray-200">
                                <div className="flex justify-between text-gray-700">
                                    <span>Subtotal</span>
                                    <span>₹{orderSummary.subtotal}</span>
                                </div>
                                <div className="flex justify-between text-green-600 font-medium">
                                    <span>Discount</span>
                                    <span>-₹{orderSummary.discount}</span>
                                </div>
                                <div className="flex justify-between text-gray-700">
                                    <span>Shipping</span>
                                    <span className="text-green-600 font-medium">FREE</span>
                                </div>
                            </div>

                            {/* Total */}
                            <div className="flex justify-between items-center text-xl font-bold">
                                <span className="text-gray-900">Total</span>
                                <span className="text-2xl bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                                    ₹{orderSummary.total}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Checkout;
